// The app: what index.tsx used to be. index.tsx requires this once the loading animation is drawing (see there). `./demo/preload` must stay the first import.
import "./demo/preload";
// a shared clip's viewer lays the stage out for the area between its bar and its controls: this must run before anything reads the window height
import "./share/installViewport";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import App from "./App";
import store, { persistor } from "./state_management/reducers/index";
import { isShareView } from "./share/shareView";

// PersistGate added 2026-08-14 - see reducers/index.tsx's own comment on
// persistor for the real bug this closes (a genuine race between
// redux-persist's async rehydration and the app's own fresh on-mount
// fetches, where stale persisted data could silently win and overwrite
// fresh server data, surviving even a hard reload). loading={null} means
// no visible spinner - rehydration from localStorage is normally fast
// enough that a loading flash would be more disruptive than the brief
// blank moment this can introduce, and correctness (never rendering
// against a stale-then-overwritten store) matters more here than shaving
// that moment off first paint.
// DEMO BUILD ONLY (REACT_APP_DEMO=1): start already logged in as the demo user. Removed from normal builds at build time.
if (process.env.REACT_APP_DEMO === "1") {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { demoUser } = require("./demo/demoBackend");
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const ActionType = require("./resources/enums").default;
  (store as any).dispatch({ type: ActionType.LOGIN, payload: demoUser });
  // The login above happens before redux-persist rehydrates, and the rehydrated copy from an EARLIER visit wins over it: an account record saved before the demo user had a creation day has none, and the
  // "All" range then had no start to name ("Invalid Date - Today"). Once the store is rehydrated, the demo user's creation day is put in (only that field: nothing else the person changed is touched).
  const putCreationDayIn = (): boolean => {
    if (!(persistor as any).getState().bootstrapped) return false;
    (store as any).dispatch({ type: ActionType.UPDATEPROFILE, payload: { createdAt: demoUser.createdAt } });

    return true;
  };
  if (!putCreationDayIn()) {
    const stop = (persistor as any).subscribe(() => { if (putCreationDayIn()) stop(); });
  }
  // the demo's history must exist BEFORE the dashboard first asks for it
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require("./demo/demoRuntime").startDemoTraffic();
}

// A RECIPIENT'S VIEWER (a shared replay clip's page, in a build with sharing on): a stand-in viewer, never the recipient's own login, never saved to their browser (src/share/viewerBoot.ts).
if (process.env.REACT_APP_DEMO !== "1" && isShareView()) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require("./share/viewerBoot").bootShareViewer(store, persistor);
}

// How did this visit arrive (campaign tags, the linking site)? Remembered here, before anything renders, so the signup form can report it: this is what lets the admin Growth page say which
// sources produce customers. Not in the demo (it has no signup). Never throws: see util/signupSource.ts.
if (process.env.REACT_APP_DEMO !== "1" && !isShareView()) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require("./util/signupSource").captureSignupSource();
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </PersistGate>
    </Provider>
   </React.StrictMode>
);

// The boot loader (public/index.html) has been playing since the first paint, while this bundle downloaded and ran. Once the app has drawn its first screen it hands over to the
// app's own loaders (which are the same animation). Two animation frames after the render, so the app's own screen is actually on the page when the boot loader fades.
requestAnimationFrame(() => requestAnimationFrame(() => (window as unknown as { __bootLoader?: { hide: () => void } }).__bootLoader?.hide()));

// // If you want your app to work offline and load faster, you can change
// // unregister() to register() below. Note this comes with some pitfalls.
// // Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.register();
